import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Radar,
  CreditCard,
  Loader2,
  LogOut,
  Check,
  AlertTriangle,
  Ban,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { openCustomerPortal } from "@/lib/api";

const STATUS_META = {
  trialing:   { label: "Trialing",   color: "#10b981", icon: Check,          hint: "Your 14-day free trial is active." },
  active:     { label: "Active",     color: "#10b981", icon: Check,          hint: "Your subscription is current." },
  past_due:   { label: "Past due",   color: "#f59e0b", icon: AlertTriangle,  hint: "We couldn't charge your card — update payment in the billing portal." },
  canceled:   { label: "Canceled",   color: "#f43f5e", icon: Ban,            hint: "Your subscription has ended. Resubscribe below." },
  incomplete: { label: "Incomplete", color: "#f59e0b", icon: AlertTriangle,  hint: "Your last checkout didn't finish. Try again from the paywall." },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [portalLoading, setPortalLoading] = useState(false);

  const status = user?.subscription_status || "none";
  const meta = STATUS_META[status];
  const Icon = meta?.icon || CreditCard;

  const onSignOut = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const onManageBilling = async () => {
    setPortalLoading(true);
    try {
      const r = await openCustomerPortal();
      if (r?.portal_url) {
        window.location.href = r.portal_url;
        return;
      }
      toast.error("Couldn't open the billing portal.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Portal unavailable.");
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div
      data-testid="account-page"
      className="min-h-screen bg-[#0b1120] px-6 py-10 text-[#f8fafc]"
    >
      <header className="mx-auto flex max-w-[720px] items-center justify-between">
        <Link to="/feed" className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center border border-[#3b82f6]/40 bg-[#3b82f6]/10">
            <Radar className="h-4 w-4 text-[#3b82f6]" />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-bold tracking-tight">SIGNAL EDGE</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#64748b]">
              account
            </div>
          </div>
        </Link>
        <button
          type="button"
          onClick={onSignOut}
          data-testid="account-signout-button"
          className="inline-flex items-center gap-1.5 border border-[#1e293b] bg-[#111827] px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-[#94a3b8] transition-colors hover:border-[#334155] hover:text-[#f8fafc]"
        >
          <LogOut className="h-3 w-3" /> Sign out
        </button>
      </header>

      <main className="mx-auto mt-8 max-w-[720px] space-y-5">
        {/* Profile card */}
        <div className="border border-[#1e293b] bg-[#111827] p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">
            Profile
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span
              data-testid="account-email"
              className="text-lg font-bold tracking-tight text-[#f8fafc]"
            >
              {user?.email || "—"}
            </span>
            <span className="font-mono text-[11px] text-[#64748b]">
              · {user?.role || "user"}
            </span>
          </div>
          <div className="mt-1 text-xs text-[#94a3b8]">
            Joined {fmtDate(user?.created_at)}
          </div>
        </div>

        {/* Subscription card */}
        <div className="border border-[#1e293b] bg-[#111827] p-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">
            Subscription
          </div>
          <div className="mt-3 flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Icon
                className="h-4 w-4"
                style={{ color: meta?.color || "#94a3b8" }}
              />
              <span
                data-testid="account-subscription-status"
                className="text-lg font-bold tracking-tight"
                style={{ color: meta?.color || "#f8fafc" }}
              >
                {meta?.label || "No subscription"}
              </span>
            </div>
            <div className="text-xs text-[#94a3b8]">
              {meta?.hint ||
                "Start a 14-day free trial to unlock the feed."}
            </div>
            {user?.current_period_end && (
              <div className="mt-2 text-[11px] text-[#64748b]">
                Next renewal: {fmtDate(user.current_period_end)}
              </div>
            )}
          </div>

          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            {["trialing", "active", "past_due"].includes(status) ? (
              <button
                type="button"
                onClick={onManageBilling}
                disabled={portalLoading}
                data-testid="account-portal-button"
                className="inline-flex flex-1 items-center justify-center gap-2 border border-[#3b82f6] bg-[#3b82f6] px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#2563eb] disabled:opacity-60"
              >
                {portalLoading ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Opening portal…
                  </>
                ) : (
                  <>
                    <CreditCard className="h-3.5 w-3.5" /> Manage billing
                  </>
                )}
              </button>
            ) : (
              <Link
                to="/paywall"
                data-testid="account-subscribe-button"
                className="inline-flex flex-1 items-center justify-center gap-2 border border-[#3b82f6] bg-[#3b82f6] px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#2563eb]"
              >
                <CreditCard className="h-3.5 w-3.5" />
                {status === "canceled" ? "Resubscribe" : "Start free trial"}
              </Link>
            )}
            {["trialing", "active"].includes(status) && (
              <Link
                to="/feed"
                data-testid="account-back-to-feed"
                className="inline-flex items-center justify-center gap-2 border border-[#1e293b] bg-[#0f172a] px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#f8fafc] transition-colors hover:border-[#334155]"
              >
                Back to feed
              </Link>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
